import React, { useState, useEffect } from "react";
import { LoginUser } from "../auth/authService";
import { Header } from "./Header";
import { VerificationService } from "../services/verificationService";
import { Session } from "../auth/session";

interface DashboardProps {
  user: LoginUser | null;
  onLogout: () => void;
  onViewHistory: () => void;
  onRequestVerification: () => void;
  onPreApprove: () => void;
  onAuthenticate: () => void;
  onSelect: (id: number) => void;
  isCompose?: boolean;
}

interface EmailDetails {
  subject: string;
  sender: string;
  senderAddress: string;
  senderDomain: string;
  bodyPreview: string;
}

type ActivityStatus = "pending" | "auth-needed" | "verified";

interface ActivityItem {
  id: number;
  title: string;
  subtitle: string;
  time: string;
  status: ActivityStatus;
  icon: "invoice" | "lock" | "check";
}

const statusConfig: Record<
  ActivityStatus,
  { label: string; iconBg: string; iconColor: string; badgeBg: string; badgeColor: string }
> = {
  pending: {
    label: "Pending",
    iconBg: "bg-[#FA9D30]/10",
    iconColor: "text-[#FA9D30]",
    badgeBg: "bg-[#FA9D30]/10",
    badgeColor: "text-[#FA9D30]",
  },
  "auth-needed": {
    label: "Auth needed",
    iconBg: "bg-[#33ccbe]/10",
    iconColor: "text-[#33ccbe]",
    badgeBg: "bg-[#33ccbe]/10",
    badgeColor: "text-[#33ccbe]",
  },
  verified: {
    label: "Verified",
    iconBg: "bg-[#25c279]/10",
    iconColor: "text-[#25c279]",
    badgeBg: "bg-[#25c279]/10",
    badgeColor: "text-[#25c279]",
  },
};

// Map API status → ActivityStatus
const toActivityStatus = (status: string | number): ActivityStatus => {
  const key =
    typeof status === "number"
      ? ({ 1: "pending", 2: "verified", 3: "auth-needed" } as Record<number, ActivityStatus>)[
          status
        ]
      : (status as string).toLowerCase();
  if (key === "verified") return "verified";
  if (key === "rejected" || key === "auth-needed") return "auth-needed";
  return "pending";
};

// Relative time
const relativeTime = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const InvoiceIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const ShieldIcon = ({ size = 14, color = "currentColor" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const AlertIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="flex-shrink-0"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const MailIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const SendIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const ActivityIcon: React.FC<{ icon: ActivityItem["icon"]; statusKey: ActivityStatus }> = ({
  icon,
  statusKey,
}) => {
  const cfg = statusConfig[statusKey];
  return (
    <div
      className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${cfg.iconBg} ${cfg.iconColor}`}
    >
      {icon === "invoice" && <InvoiceIcon />}
      {icon === "lock" && <LockIcon />}
      {icon === "check" && <CheckCircleIcon />}
    </div>
  );
};

const Dashboard: React.FC<DashboardProps> = ({
  user,
  onLogout,
  onViewHistory,
  onRequestVerification,
  onPreApprove,
  onAuthenticate,
  onSelect,
  isCompose = false,
}) => {
  const [emailDetails, setEmailDetails] = useState<EmailDetails | null>(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [popupInfo, setPopupInfo] = useState<{ title: string; message: string } | null>(null);

  // Real verification data
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [verifiedTodayCount, setVerifiedTodayCount] = useState<number | null>(null);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [loadingStats, setLoadingStats] = useState(true);

  // Fetch verifications and derive stats
  useEffect(() => {
    const fetchStats = async () => {
      setLoadingStats(true);
      try {
        const token = await Session.getToken();
        if (!token) return;
        const response = await VerificationService.listAll(token);
        if (response.data) {
          const envelope = response.data as any;
          const list: any[] = Array.isArray(envelope) ? envelope : (envelope?.data ?? []);

          // Pending count
          const pending = list.filter((v) => {
            const s = toActivityStatus(v.status);
            return s === "pending";
          }).length;
          setPendingCount(pending);

          // Verified today count
          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);
          const verifiedToday = list.filter((v) => {
            const s = toActivityStatus(v.status);
            return s === "verified" && new Date(v.updatedAt) >= todayStart;
          }).length;
          setVerifiedTodayCount(verifiedToday);

          // Recent activity — last 3, sorted newest first
          const sorted = [...list].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          const recent: ActivityItem[] = sorted.slice(0, 3).map((v) => {
            const actStatus = toActivityStatus(v.status);
            // Pick icon by status
            const icon: ActivityItem["icon"] =
              actStatus === "verified" ? "check" : actStatus === "auth-needed" ? "lock" : "invoice";
            // Primary display value: first data value or title
            const primaryValue =
              v.data?.sender_address ||
              v.data?.domain ||
              (v.data ? Object.values(v.data as Record<string, string>).find(Boolean) : null) ||
              v.title ||
              `Request #${v.id}`;
            const assigneeName = (() => {
              const c = v.assignments?.[0]?.user?.contact;
              if (!c) return null;
              return [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email;
            })();
            return {
              id: v.id,
              title: primaryValue,
              subtitle: assigneeName ? `Assigned to ${assigneeName}` : (v.type ?? ""),
              time: relativeTime(v.createdAt),
              status: actStatus,
              icon,
            };
          });
          setRecentActivity(recent);
        }
      } catch {
        // silently fail — stats just won't show
      } finally {
        setLoadingStats(false);
      }
    };
    fetchStats();
  }, []);

  const loadEmailDetails = () => {
    setLoadingEmail(true);
    try {
      const item = Office.context?.mailbox?.item;
      if (!item) {
        setEmailDetails(null);
        setLoadingEmail(false);
        return;
      }
      const subject = item.subject || "No Subject";
      const sender = item.from?.displayName || "Unknown Sender";
      const senderAddress = item.from?.emailAddress || "";
      const senderDomain = senderAddress.includes("@") ? senderAddress.split("@")[1] : "";

      item.body.getAsync(Office.CoercionType.Text, (result) => {
        const body =
          result.status === Office.AsyncResultStatus.Succeeded
            ? result.value
            : "Could not load body preview.";
        const preview = body.length > 160 ? body.substring(0, 160) + "..." : body;
        setEmailDetails({ subject, sender, senderAddress, senderDomain, bodyPreview: preview });
        setLoadingEmail(false);
      });
    } catch {
      setEmailDetails(null);
      setLoadingEmail(false);
    }
  };

  useEffect(() => {
    loadEmailDetails();
    const handleItemChanged = () => setTimeout(loadEmailDetails, 200);
    Office.context?.mailbox?.addHandlerAsync(Office.EventType.ItemChanged, handleItemChanged);
    return () => {
      Office.context?.mailbox?.removeHandlerAsync(Office.EventType.ItemChanged, handleItemChanged);
    };
  }, []);

  const initials = emailDetails?.sender
    ? emailDetails.sender
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  return (
    <div className="w-full bg-white font-sans relative">
      <Header title="for Outlook" onLogout={onLogout} />

      {/* Modal Popup */}
      {popupInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl border border-gray-100 p-6 w-full max-w-sm shadow-xl animate-scale-up text-center">
            <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-amber-500">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-[15px] font-bold text-gray-900 mb-2">{popupInfo.title}</h3>
            <p className="text-[12px] text-gray-500 leading-relaxed mb-6 whitespace-pre-line">
              {popupInfo.message}
            </p>
            <button
              onClick={() => setPopupInfo(null)}
              className="w-full py-3.5 bg-[#33ccbf] hover:bg-[#2bbdb1] active:scale-[0.98] text-white rounded-2xl text-[13px] font-bold transition-all shadow-md shadow-[#33ccbf]/25"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      <div className="p-3">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-2 mb-3.5">
          <div className="bg-gray-50 flex flex-col items-center rounded-lg px-3 py-2">
            <div className="text-xl font-semibold text-[#FF9E2C] leading-none">
              {loadingStats ? (
                <span className="text-base text-gray-300">...</span>
              ) : (
                (pendingCount ?? 0)
              )}
            </div>
            <div className="text-[11px] text-gray-400 mt-1">Pending</div>
          </div>

          <div className="bg-gray-50 flex flex-col items-center rounded-lg px-3 py-2">
            <div className="text-xl font-semibold text-[#33ccb8] leading-none">
              {loadingStats ? (
                <span className="text-base text-gray-300">...</span>
              ) : (
                (verifiedTodayCount ?? 0)
              )}
            </div>
            <div className="text-[11px] text-gray-400 mt-1">Verified today</div>
          </div>
        </div>

        {/* Current Email */}
        {/* <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
          Current email
        </p>
        <div className="border border-gray-100 rounded-xl p-3 mb-3.5">
          {emailDetails ? (
            <>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center text-[14px] font-semibold flex-shrink-0">
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-gray-900 truncate">
                    {emailDetails.sender}
                  </div>
                </div>
                <button
                  onClick={loadEmailDetails}
                  disabled={loadingEmail}
                  className="border border-gray-200 rounded-md px-2 py-1 text-[11px] text-gray-500 bg-transparent hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  {loadingEmail ? "..." : "↺"}
                </button>
              </div>
              <div className="text-[12px] font-semibold text-gray-900 mb-1 truncate">
                {emailDetails.subject}
              </div>
              <div className="text-[11px] text-gray-400 leading-relaxed line-clamp-2">
                {emailDetails.bodyPreview}
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-2 bg-amber-50 rounded-lg mt-2 text-amber-700">
                <AlertIcon />
                <span className="text-[11px] font-semibold">No verification on this email yet</span>
              </div>
            </>
          ) : (
            <p className="text-[12px] text-gray-300 text-center py-3">
              {loadingEmail ? "Loading email..." : "No email selected"}
            </p>
          )}

          <button
            onClick={onRequestVerification}
            className="w-full mt-2.5 py-2.5 bg-[#33ccbf] hover:bg-[#2bbdb1] text-white border-none rounded-lg text-[13px] font-semibold flex items-center justify-center gap-1.5 transition-colors active:scale-[0.98] cursor-pointer"
          >
            <ShieldIcon size={14} color="white" />
            Request verification
          </button>
        </div> */}

        {/* Activity */}
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
            Email activity
          </p>
          <button
            onClick={onViewHistory}
            className="text-[11px] text-[#33ccbf] font-semibold bg-transparent border-none cursor-pointer hover:underline"
          >
            View all
          </button>
        </div>

        <div className="border border-gray-100 rounded-xl px-2.5 mb-3.5">
          {loadingStats ? (
            <div className="py-4 text-center text-[12px] text-gray-300">Loading...</div>
          ) : recentActivity.length === 0 ? (
            <div className="py-4 text-center text-[12px] text-gray-300">No activity yet</div>
          ) : (
            recentActivity.map((item, idx) => {
              const cfg = statusConfig[item.status];
              return (
                <div
                  key={item.id}
                  onClick={() => onSelect(item.id)}
                  className={`flex items-center justify-center gap-2.5 py-2.5 cursor-pointer hover:opacity-75 h-20 transition-opacity ${
                    idx < recentActivity.length - 1 ? "border-b border-gray-100" : ""
                  }`}
                >
                  <ActivityIcon icon={item.icon} statusKey={item.status} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1.5">
                      <span className="text-[12px] font-semibold text-gray-900 truncate max-w-[155px]">
                        {item.title}
                      </span>
                      <span
                        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${cfg.badgeBg} ${cfg.badgeColor}`}
                      >
                        {cfg.label}
                      </span>
                    </div>
                    <span className="text-[11px] text-gray-400">
                      {item.subtitle} · {item.time}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* New Request */}
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
          New request
        </p>

        {[
          {
            label: "Verify received email",
            desc: "Confirm this email is legitimate",
            iconBg: "bg-[#33ccbe]/10",
            iconColor: "text-[#33ccbe]",
            disabled: isCompose,
            action: isCompose
              ? () =>
                  setPopupInfo({
                    title: "Action Unavailable",
                    message:
                      "To verify a received email, close the app, open the email you want to work with in Outlook, and then reopen the app.",
                  })
              : onRequestVerification,
            icon: <MailIcon />,
          },
          {
            label: "Pre-approve to send",
            desc: "Get approval before a sensitive send",
            iconBg: "bg-[#FF9E2C]/10",
            iconColor: "text-[#FF9E2C]",
            disabled: !isCompose,
            action: !isCompose
              ? () =>
                  setPopupInfo({
                    title: "Action Unavailable",
                    message:
                      "To continue, close the app, open or start composing your email in Outlook, and then reopen the app to pre-approve it.",
                  })
              : onPreApprove,
            icon: <SendIcon />,
          },
        ].map(({ label, desc, iconBg, iconColor, disabled, action, icon }) => (
          <button
            key={label}
            onClick={action}
            className={`flex items-center h-20 gap-2.5 px-2.5 py-2.5 bg-white border border-gray-100 rounded-xl cursor-pointer w-full text-left mb-2 transition-all active:scale-[0.99] ${
              disabled ? "opacity-50 hover:bg-gray-50/50" : "hover:bg-gray-50 hover:border-gray-200"
            }`}
          >
            <div
              className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${iconBg} ${iconColor}`}
            >
              {icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold text-gray-900">{label}</div>
              <div className="text-[11px] text-gray-400 mt-0.5">{desc}</div>
            </div>
            <span className="text-gray-300 flex-shrink-0">
              <ChevronRightIcon />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
